import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

import { API_BASE } from "../lib/apiBase";
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper function to format API error details
function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  // Family state — parent login can switch between children profiles.
  const [family, setFamily] = useState(null);   // { id, name, members[] } | null
  const [activeProfileId, setActiveProfileId] = useState(null); // user.id of the active child/self

  const API_URL = API_BASE;

  // Check if user is already logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Whenever the authenticated user changes, (re)load their family + resolve
  // the active profile from localStorage (if the stored id is still a valid member).
  useEffect(() => {
    const loadFamily = async () => {
      if (!user || !user.id) { setFamily(null); setActiveProfileId(null); return; }
      try {
        const { data } = await axios.get(`${API_URL}/api/families/mine`, {
          withCredentials: true,
          validateStatus: (s) => s < 500,
        });
        const fam = data && data.id ? data : null;
        setFamily(fam);
        // Resolve active profile id.
        let nextId = user.id;
        if (fam && Array.isArray(fam.members) && fam.members.length > 0) {
          try {
            const stored = localStorage.getItem(`active_profile_id:${user.id}`);
            if (stored && fam.members.some((m) => m.id === stored)) {
              nextId = stored;
            }
          } catch { /* silent */ }
        }
        setActiveProfileId(nextId);
      } catch {
        setFamily(null);
        setActiveProfileId(user.id);
      }
    };
    loadFamily();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const switchProfile = (profileId) => {
    if (!family || !family.members.some((m) => m.id === profileId)) return;
    setActiveProfileId(profileId);
    try { localStorage.setItem(`active_profile_id:${user.id}`, profileId); } catch { /* silent */ }
  };

  const activeProfile = (() => {
    if (!user) return null;
    if (family && Array.isArray(family.members)) {
      const m = family.members.find((x) => x.id === activeProfileId);
      if (m) return m;
    }
    return user;
  })();

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        // 401 for guests is EXPECTED — prevent axios from logging it as an error.
        validateStatus: (s) => s < 500,
      });
      if (data && data.id) {
        setUser(data);
      } else {
        setUser(false);
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        validateStatus: (s) => s < 500,
      });
      if (data && data.id) {
        setUser(data);
        return data;
      }
      return null;
    } catch {
      return null;
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      // No response object → network/CORS error. Surface a clearer message so
      // production issues (wrong REACT_APP_BACKEND_URL, blocked CORS, etc.)
      // are diagnosable from the UI alone instead of the generic fallback.
      if (!error.response) {
        return {
          success: false,
          error: "Impossible de joindre le serveur. Vérifiez votre connexion ou réessayez dans quelques instants.",
        };
      }
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: errorMessage };
    }
  };

  const register = async (payloadOrEmail, password, name, role = 'student') => {
    try {
      // Backwards compatible: accept either a full payload object or (email, password, name, role)
      const payload = typeof payloadOrEmail === 'object' && payloadOrEmail !== null
        ? payloadOrEmail
        : { email: payloadOrEmail, password, name, role };
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        payload,
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await axios.post(
        `${API_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
      setUser(false);
    } catch (error) {
      console.error('Logout error:', error);
      setUser(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      return { success: true };
    } catch (error) {
      const errorMessage = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: errorMessage };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    refreshUser,
    logout,
    forgotPassword,
    resetPassword,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    family,
    activeProfile,
    activeProfileId,
    switchProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
